// @flow
import * as React from 'react';
import Select, { components } from 'react-select';
import type { InputActionMeta, ValueType } from 'react-select/src/types';
import {
    defaultsDeep, memoize, debounce, isEqual,
} from 'lodash';
import { connectAPI } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { Heading, Box } from 'grommet';
import Spinner from 'speed-js-react-web/src/components/Spinner';
import memoizeOne from 'memoize-one';


type Props = {
    choices: {
        [any]: string,
    },
    onChange: (value: ValueType, _original: Object) => void,
    onBlur: (e: SyntheticFocusEvent<any>) => void,
    onOptionsLoad?: (options: { value: string, label: string, _original: Object }[]) => void,
    value: any;
    name: string;
    multi: boolean,
    // eslint-disable-next-line
    resourceName: string,
    // eslint-disable-next-line
    resourceMethod: string,
    // eslint-disable-next-line
    initialOptionResourceMethod: string,
    // eslint-disable-next-line
    searchField: string,
    // eslint-disable-next-line
    additionalFilters: Object,
    // eslint-disable-next-line
    valueField: string,
    // eslint-disable-next-line
    labelFields: Array<string>,
    formatLabel: (option: Object, props: Props) => string,
    minSearchLength: number,
    preload: boolean,
    cacheOptions?: boolean,
    setFirstVal: Function,
    resultsCountLabel: string | React.Node,
    placeholder?: any,
    initialObjectId: any,
    customSelectStyles?: Object,
    noOptionsMessage?: () => React.Node,
}

type State = {
    loading: boolean,
    options: Array<{ label: string, value: any }>,
    loadedOptions: [],
}

type CombinedProps = Props & {
    getData: (searchText: string) => Promise<Object>,
    getInitialOption: (value: string) => Promise<Object>,
}


const defaultFormatLabel = (option: Object, props: Props) => {
    let label = '';

    props.labelFields.forEach((lf) => {
        if (option[lf]) label = `${label} ${option[lf]}`;
    });

    return label;
};

const DropdownIndicator = (
    props: React.ElementConfig<typeof components.DropdownIndicator>,
) => (
    <components.DropdownIndicator {...props}>
        <span />
    </components.DropdownIndicator>
);

const MenuList = (
    props: React.ElementConfig<typeof components.MenuList>,
) => (
    <components.MenuList {...props}>
        {props.isLoading ? (
            <Spinner
                type="clipLoader"
                loading
                blocking={false}
                color="black"
                coverStyle={{
                    position: 'relative',
                    width: 'auto',
                    height: 'auto',
                }}
            />
        ) : (props.children)}
    </components.MenuList>
);

const groupStyles = {
    border: '2px dotted black',
    borderRadius: '5px',
    background: '#f2fcff',
};

const SelectContainer = (
    // eslint-disable-next-line react/prop-types
    { children, ...props },
) => (
    <components.SelectContainer {...props} style={groupStyles}>
        {children}
    </components.SelectContainer>
);

const selectStyles = {
    menu: (base) => ({
        marginTop: '-1px',
        borderRadius: '0px 0px 4px 4px',
        backgroundColor: 'hsl(0, 0%, 100%)',
        border: '1px solid #d6d6d6',
    }),
    control: (base) => ({
        borderRadius: '4px 4px 0px 0px',
        border: '1px solid #d6d6d6',
    }),
    menuList: (base) => ({ ...base, maxHeight: '203px' }),
};

const COMPONENTS = { DropdownIndicator, SelectContainer, MenuList };


@connectAPI((apiClient: ApiClient, props: Props) => ({
    getData: {
        send: (searchText: Object) => apiClient.resources[props.resourceName][props.resourceMethod]({
            [props.searchField]: searchText,
            ...props.additionalFilters,
        }),
    },
    getInitialOption: {
        send: (value: string) => (
            apiClient.resources[props.resourceName][props.initialOptionResourceMethod](value)
        ),
    },
}), true)
class ListSelect extends React.Component<CombinedProps, State> {
    select: { current: ?typeof Select };

    getSelectStyles: (styles: Object) => Object;

    cachedOptions = {};

    constructor(props: CombinedProps) {
        super(props);

        this.state = {
            options: [],
            loading: false,
        };

        this.getValue = memoizeOne(this.getValue);
        this.loadOptions = debounce(this.loadOptions, 200);

        this.select = React.createRef();
        this.getSelectStyles = memoize((styles: Object) => defaultsDeep(styles, selectStyles));
    }

    componentDidMount() {
        const { initialObjectId } = this.props;

        if (initialObjectId) {
            this.loadInitialOption();
        } else {
            this.loadOptions('');
        }
    }

    componentDidUpdate(prevProps: CombinedProps) {
        const {
            resourceName, resourceMethod, additionalFilters, preload,
        } = this.props;

        if (
            resourceName !== prevProps.resourceName
            || resourceMethod !== prevProps.resourceMethod
            || !isEqual(additionalFilters, prevProps.additionalFilters)
        ) {
            if (preload) {
                this.clearCache();
                this.loadOptions('');
            }
        }
    }

    getValue = (value: any, options: Array<{ label: string, value: any }>): any => {
        const val = value ? value.toString() : value;
        return options.find((opt) => opt.value === val) || null;
    };

    getOptionValue = (option: { label: string, value: any }) => option.value || '';

    handleChange = (value?: Object) => {
        const { onChange, multi } = this.props;

        if (multi) {
            onChange(value ? value.map((v) => v.value) : null, value ? value.map((v) => v._original) : null);
        } else {
            onChange(value ? value.value : null, value ? value._original : null);
        }
    };

    handleInputChange = (inputValue: string, actionMeta: InputActionMeta) => {
        if (actionMeta.action === 'input-change') {
            this.loadOptions(inputValue, true);
        }
    };

    setInitialOptions = (response) => {
        const {
            formatLabel, valueField, setFirstVal,
        } = this.props;

        if (response && response[valueField]) {
            const options = [];

            const getLabel = formatLabel || defaultFormatLabel;

            options.push({
                label: getLabel(response, this.props),
                value: response[valueField].toString(),
                _original: response,
            });


            const valueResults = options.filter((v) => (v.value != null));
            if (setFirstVal && valueResults.length === 1) {
                setFirstVal(valueResults[0].value);
            }

            this.setState({
                options,
                loading: false,
            });
        }
    };

    loadInitialOption = () => {
        const { getInitialOption, initialObjectId: objectId } = this.props;

        this.setState({ loading: true });
        getInitialOption(objectId).then((response: Object) => this.setInitialOptions(response));
    };

    setOptions = (response, searchText) => {
        const {
            cacheOptions, onOptionsLoad,
        } = this.props;

        const {
            formatLabel, valueField,
        } = this.props;

        const options = [];

        if (response.results) {
            const getLabel = formatLabel || defaultFormatLabel;

            response.results.forEach((res) => {
                options.push({
                    label: getLabel(res, this.props),
                    value: res[valueField].toString(),
                    _original: res,
                });
            });
        }

        if (cacheOptions) {
            this.cachedOptions[searchText] = options;
        }

        this.setState({
            loadedOptions: options,
            options,
            loading: false,
        });

        if (onOptionsLoad) {
            onOptionsLoad(options);
        }
    };

    customOptionsFilter = (option, searchText) => option.label.toString().replace(/ /g, '').toLowerCase()
        .includes(searchText.toString().replace(/ /g, '').toLowerCase());

    loadOptions = (searchText: string, fromInputChange: boolean = false) => {
        const {
            getData, minSearchLength, cacheOptions, onOptionsLoad,
        } = this.props;
        const { options, loadedOptions } = this.state;

        if (searchText.length >= minSearchLength) {
            if (cacheOptions && typeof this.cachedOptions[searchText] !== 'undefined') {
                this.setState({
                    options: this.cachedOptions[searchText],
                });
                if (onOptionsLoad) {
                    onOptionsLoad(this.cachedOptions[searchText]);
                }
            }
            if (fromInputChange && loadedOptions && loadedOptions.length > 0) {
                const filtered = loadedOptions.filter((val) => {
                    if (searchText === '') {
                        return true;
                    }
                    return val.label.toString().replace(/ /g, '').toLowerCase()
                        .includes(searchText.toString().replace(/ /g, '').toLowerCase());
                });

                this.setState({ options: filtered });
            }

            if (!fromInputChange) {
                this.setState({ loading: true });
                getData(searchText).then((response: Object) => this.setOptions(response, searchText));
            }
        }
    };

    clearCache() {
        this.cachedOptions = {};
    }

    render() {
        const {
            name, onBlur, value, multi, resultsCountLabel, placeholder, customSelectStyles, noOptionsMessage,
        } = this.props;

        const { loading, options } = this.state;

        return (
            <>
                { resultsCountLabel
                && (
                    <Box margin={{ bottom: 'xsmall' }}>
                        <Heading level={5}>
                            {resultsCountLabel}
                            {': '}
                            <b>{options.length}</b>
                        </Heading>
                    </Box>
                )
                }
                <Select
                    placeholder=""
                    options={options}
                    onChange={this.handleChange}
                    value={this.getValue(value, options)}
                    name={name}
                    id={`id-${name}`}
                    isMulti={multi}
                    onBlur={onBlur}
                    getOptionValue={this.getOptionValue}
                    onInputChange={this.handleInputChange}
                    isLoading={loading}
                    ref={this.select}
                    menuIsOpen
                    styles={this.getSelectStyles(customSelectStyles)}
                    autoFocus
                    filterOption={this.customOptionsFilter}
                    components={COMPONENTS}
                    noOptionsMessage={noOptionsMessage}
                    maxMenuHeight={150}
                />
            </>
        );
    }
}

ListSelect.defaultProps = {
    searchField: 'search',
    resourceMethod: 'list',
    initialOptionResourceMethod: 'get',
    valueField: 'id',
    labelFields: null,
    formatLabel: null,
    minSearchLength: 3,
    additionalFilters: {},
    preload: false,
    cacheOptions: true,
    customSelectStyles: {},
    noOptionsMessage: undefined,
    onOptionsLoad: undefined,
    placeholder: '',
};

export default ListSelect;
